import React, { useMemo } from "react";
import { useTheme } from "@material-ui/core/styles";
import {
	BarChart,
	CartesianGrid,
	Bar,
	Cell,
	XAxis,
	YAxis,
	Label,
	ResponsiveContainer,
	Tooltip
} from "recharts";
import { Grid, Typography } from "@material-ui/core";
import { format } from "date-fns";

import useTickets from "../../hooks/useTickets";

const HOURS_LIST = [
	"00:00", "01:00", "02:00", "03:00", "04:00", "05:00",
	"06:00", "07:00", "08:00", "09:00", "10:00", "11:00",
	"12:00", "13:00", "14:00", "15:00", "16:00", "17:00",
	"18:00", "19:00", "20:00", "21:00", "22:00", "23:00"
];

// Segunda primeiro (convenção de negócio), índice = Date.getDay() (0=Dom..6=Sáb)
const WEEKDAYS = [
	{ dow: 1, label: "Seg" },
	{ dow: 2, label: "Ter" },
	{ dow: 3, label: "Qua" },
	{ dow: 4, label: "Qui" },
	{ dow: 5, label: "Sex" },
	{ dow: 6, label: "Sáb" },
	{ dow: 0, label: "Dom" },
];

const MIN_CONFIDENT_OCCURRENCES = 3;

// Sempre usa todo o histórico disponível — sem seletor de período. Faz mais
// sentido pra "achar o horário/dia de pico" do que qualquer janela fixa
// (7/15/30 dias), já que hoje (14/08/2026) o projeto só tem ~8 dias de dado
// no banco, e a métrica fica melhor sozinha conforme a base cresce.
const Chart = ({ whatsappId = "", startDate = "", endDate = "" }) => {
	const theme = useTheme();

	const { tickets } = useTickets({
		showAll: "true",
		whatsappId: whatsappId || undefined,
		startDate: startDate || undefined,
		endDate: endDate || undefined,
	});

	const { hourlyData, weekdayData, rangeLabel, hasLowConfidence } = useMemo(() => {
		const now = new Date();

		if (tickets.length === 0) {
			return { hourlyData: [], weekdayData: [], rangeLabel: "", hasLowConfidence: false };
		}

		const timestamps = tickets.map(t => new Date(t.createdAt).getTime());
		const minDate = new Date(Math.min(...timestamps));
		const startDay = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
		const endDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		const totalDays = Math.max(1, Math.round((endDay - startDay) / (1000 * 60 * 60 * 24)) + 1);

		// Movimento por hora: total de tickets naquela hora / dias corridos observados
		const hourCounts = {};
		HOURS_LIST.forEach(h => { hourCounts[h] = 0; });
		tickets.forEach(ticket => {
			const hourStr = `${new Date(ticket.createdAt).getHours().toString().padStart(2, "0")}:00`;
			if (hourCounts[hourStr] !== undefined) hourCounts[hourStr]++;
		});
		const hourlyData = HOURS_LIST
			.map(h => ({
				time: h,
				total: hourCounts[h],
				amount: Number((hourCounts[h] / totalDays).toFixed(1)),
			}))
			.filter(d => parseInt(d.time) >= 6 && parseInt(d.time) <= 22);

		// Movimento por dia da semana: total de tickets naquele dow / quantas
		// vezes esse dow já ocorreu na janela observada (não só nos dias com
		// ticket) — senão um dia parado infla a média dos outros.
		const weekdayOccurrences = [0, 0, 0, 0, 0, 0, 0];
		for (let d = new Date(startDay); d <= endDay; d.setDate(d.getDate() + 1)) {
			weekdayOccurrences[d.getDay()]++;
		}
		const weekdayCounts = [0, 0, 0, 0, 0, 0, 0];
		tickets.forEach(ticket => {
			weekdayCounts[new Date(ticket.createdAt).getDay()]++;
		});
		let hasLowConfidence = false;
		const weekdayData = WEEKDAYS.map(({ dow, label }) => {
			const occurrences = weekdayOccurrences[dow];
			const lowConfidence = occurrences < MIN_CONFIDENT_OCCURRENCES;
			if (lowConfidence) hasLowConfidence = true;
			return {
				day: label,
				total: weekdayCounts[dow],
				occurrences,
				amount: occurrences > 0 ? Number((weekdayCounts[dow] / occurrences).toFixed(1)) : 0,
				lowConfidence,
			};
		});

		const rangeLabel = `${format(startDay, "dd/MM")} – ${format(endDay, "dd/MM")} (${totalDays}d) · ${tickets.length} tickets`;

		return { hourlyData, weekdayData, rangeLabel, hasLowConfidence };
	}, [tickets]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Typography variant="caption" style={{ color: theme.palette.text.secondary }}>
					📊 {rangeLabel || "sem tickets no período selecionado"}
				</Typography>
			</Grid>

			<Grid item xs={12} md={6}>
				<Typography component="h3" variant="subtitle1" style={{ fontWeight: "bold", marginBottom: 4 }}>
					🔥 Movimento por Hora
				</Typography>
				<ResponsiveContainer width="100%" height={220}>
					<BarChart data={hourlyData} barSize={20} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis dataKey="time" stroke={theme.palette.text.secondary} tick={{ fontSize: 11 }} interval={1} />
						<YAxis type="number" allowDecimals stroke={theme.palette.text.secondary} width={30}>
							<Label angle={270} position="left" style={{ textAnchor: "middle", fill: theme.palette.text.primary }}>
								Média / Hora
							</Label>
						</YAxis>
						<Tooltip
							formatter={(value, name, item) => [
								`${value} por hora em média (${item.payload.total} tickets no total)`,
								"Média"
							]}
							labelFormatter={(label) => `Horário: ${label}`}
						/>
						<Bar dataKey="amount" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
					</BarChart>
				</ResponsiveContainer>
			</Grid>

			<Grid item xs={12} md={6}>
				<Typography component="h3" variant="subtitle1" style={{ fontWeight: "bold", marginBottom: 4 }}>
					📅 Movimento por Dia da Semana
				</Typography>
				<ResponsiveContainer width="100%" height={220}>
					<BarChart data={weekdayData} barSize={28} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis dataKey="day" stroke={theme.palette.text.secondary} tick={{ fontSize: 11 }} />
						<YAxis type="number" allowDecimals stroke={theme.palette.text.secondary} width={30}>
							<Label angle={270} position="left" style={{ textAnchor: "middle", fill: theme.palette.text.primary }}>
								Média / Dia
							</Label>
						</YAxis>
						<Tooltip
							formatter={(value, name, item) => [
								`${value} por dia em média (${item.payload.total} tickets em ${item.payload.occurrences}x que esse dia ocorreu)`,
								item.payload.lowConfidence ? "Média (amostra pequena)" : "Média"
							]}
							labelFormatter={(label) => `${label}-feira`}
						/>
						<Bar dataKey="amount" radius={[4, 4, 0, 0]}>
							{weekdayData.map((entry) => (
								<Cell
									key={entry.day}
									fill={theme.palette.primary.main}
									fillOpacity={entry.lowConfidence ? 0.45 : 1}
								/>
							))}
						</Bar>
					</BarChart>
				</ResponsiveContainer>
				{hasLowConfidence && (
					<Typography variant="caption" style={{ color: theme.palette.text.secondary, display: "block", marginTop: 2 }}>
						* barras claras = menos de {MIN_CONFIDENT_OCCURRENCES} ocorrências desse dia no período — padrão ainda não confirmado, confiabilidade cresce com mais semanas de dado.
					</Typography>
				)}
			</Grid>
		</Grid>
	);
};

export default Chart;
